import {keepPreviousData, queryOptions, useQuery} from "@tanstack/react-query";
import {DateRange} from "react-day-picker";

import * as apiClient from "@/lib/api";
import {useIdentity} from "@/lib/auth/hooks/identity";
import {formatDate, getGuideYear} from "@/lib/filters/date/date-utils";
import useIsQueryEnabled from "@/lib/hooks/query-enabler";
import {useQueryResetWithRefresh} from "@/lib/hooks/query-refetch-with-reset";
import {topRatedAdvisersCountQueryOptions} from "@/lib/hooks/top-rated-advisers-count";
import {
  InfluencedAndRecommendersData,
  PotentialClientsData
} from "@/modules/verified/types";
import {useDate} from "@/stores/date-filter";
import {
  QUERY_KEY_INFLUENCED_AND_RECOMMENDERS_DATA,
  QUERY_KEY_POTENTIAL_CLIENTS_DATA
} from "@/utils/keys";

function fetchPotentialClients(date: DateRange): Promise<PotentialClientsData> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')
  return apiClient.get(`/firm/dashboard/potential-clients?&to=${to}&from=${from}`)
}
export const potentialClientsQueryOptions = (date: DateRange) => queryOptions({
  queryKey: [QUERY_KEY_POTENTIAL_CLIENTS_DATA, date],
  queryFn: () => fetchPotentialClients(date),
  placeholderData: keepPreviousData
})

export const usePotentialClients = () => {
  const { date } = useDate()
  const { data: identity } = useIdentity()

  const queryOptions = potentialClientsQueryOptions(date)
  queryOptions.enabled = useIsQueryEnabled(identity?.showVerifiedStatistics ?? false)

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

function fetchInfluencedAndRecommenders(date: DateRange): Promise<InfluencedAndRecommendersData> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')
  return apiClient.get(`/firm/dashboard/influenced-and-recommenders?&to=${to}&from=${from}`)
}
export const influencedAndRecommendersQueryOptions = (date: DateRange) => queryOptions({
  queryKey: [QUERY_KEY_INFLUENCED_AND_RECOMMENDERS_DATA, date],
  queryFn: () => fetchInfluencedAndRecommenders(date),
  placeholderData: keepPreviousData
})

export const useInfluencedAndRecommenders = () => {
  const { date } = useDate()
  const { data: identity } = useIdentity()

  const queryOptions = influencedAndRecommendersQueryOptions(date)
  queryOptions.enabled = useIsQueryEnabled(identity?.showVerifiedStatistics ?? false)

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

export const useTopRatedAdvisersCount = () => {
  const { data: identity } = useIdentity()

  const query = topRatedAdvisersCountQueryOptions(getGuideYear())
  query.enabled = useIsQueryEnabled(identity?.showVerifiedStatistics ?? false)
  
  return useQuery(query)
}
